import React, { useEffect, useState } from "react";
import styles from "./styles.module.scss";
import { useFormStore } from "src/store/dataEntryForm";
import { DollarSign } from "lucide-react";
import { DatePicker } from "antd";
import CalendarIcon from "../../../assets/icons/CalendarIcon.svg";
import { LocalFormField } from "./FormFieldCOGS";
import { ProductsService } from "@services/amazon/products/products.service";
import classNames from "classnames";
import { Select } from "@components/Select";
import { dataEntryApi } from "@services/data-entry/data-entry.api";
import moment from "moment";
import { toast } from "react-toastify";

export interface COGSFormData {
  product?: string;
  currency?: string;
  cogs?: string | number;
  miscellaneous?: string | number;
  total?: string | number;
  startDate?: string;
  endDate?: string;
}

interface COGSFormProps {
  formId?: string;
  className?: string;
  onSubmit?: () => void;
  onEditCogSuccess?: (values: COGSFormData) => void;
  initialData?: COGSFormData;
}

export const COGSForm: React.FC<COGSFormProps> = ({
  formId = "cogs",
  className,
  onSubmit,
  onEditCogSuccess,
  initialData,
}) => {
  const [productOptions, setProductOptions] = useState([]);
  const { initializeForm, resetForm, updateFormField } = useFormStore();
  const productsService = new ProductsService();
  // Add state for date validation error
  const [dateError, setDateError] = useState<string | null>(null);

  const [inputData, setInputData] = useState<COGSFormData>({
    product: "",
    currency: "USD",
    cogs: 0,
    miscellaneous: 0,
    total: 0,
    startDate: "",
    endDate: "",
  });

  useEffect(() => {
    if (initialData) {
      setInputData({
        product: initialData.product ?? "",
        currency: initialData.currency ?? "USD",
        cogs: initialData.cogs ?? 0,
        miscellaneous: initialData.miscellaneous ?? 0,
        total: initialData.total ?? 0,
        startDate: initialData.startDate ?? "",
        endDate: initialData.endDate ?? "",
      });

      // Validate initial dates
      validateDates(initialData.startDate, initialData.endDate);
    }
  }, [initialData]);

  // Function to validate start and end dates
  const validateDates = (start?: string, end?: string) => {
    if (start && end) {
      const startDate = moment(start);
      const endDate = moment(end);

      if (startDate.isAfter(endDate)) {
        setDateError("Start date cannot be after end date");
        return false;
      }
    }

    setDateError(null);
    return true;
  };

  useEffect(() => {
    const getASINs = async () => {
      try {
        const res = await productsService.getOwnProducts({ limit: 5 });
        const options = res.map((item) => ({
          id: item.asin,
          text: item.item_name,
          img: item.image_link,
        }));
        setProductOptions(options);
      } catch (error) {
        console.error("Error fetching ASINs:", error);
      }
    };

    getASINs();
    updateFormField(formId, "currency", "USD");
  }, []);

  const handleProductChange = (value: string) => {
    setInputData((prev) => ({ ...prev, product: value }));
    updateFormField(formId, "product", value);
  };

  const handleCurrencyChange = (value: string) => {
    setInputData((prev) => ({ ...prev, currency: value }));
    updateFormField(formId, "currency", value);
  };

  const handleFieldChange = (fieldName: keyof COGSFormData, value: any) => {
    const numericValue = Number(value);
    setInputData((prev) => ({ ...prev, [fieldName]: numericValue }));
    updateFormField(formId, fieldName, numericValue);
  };

  const handleDateChange = (fieldName: keyof COGSFormData, date: any) => {
    const dateValue = date?.format("YYYY-MM-DD") ?? "";

    // Update the state with the new date
    const updatedData = {
      ...inputData,
      [fieldName]: dateValue,
    };

    setInputData(updatedData);
    updateFormField(formId, fieldName, dateValue);

    // Validate dates whenever either date changes
    if (fieldName === "startDate") {
      validateDates(dateValue, updatedData.endDate);
    } else if (fieldName === "endDate") {
      validateDates(updatedData.startDate, dateValue);
    }
  };

  // Add a disabledDate function to prevent selecting invalid dates
  const disabledStartDate = (current: moment.Moment | null) => {
    if (!current || !inputData.endDate) return false;
    return current > moment(inputData.endDate);
  };

  const disabledEndDate = (current: moment.Moment | null) => {
    if (!current || !inputData.startDate) return false;
    return current < moment(inputData.startDate);
  };

  useEffect(() => {
    initializeForm(formId, { ...inputData });
    return () => resetForm(formId);
  }, [formId, initializeForm, resetForm, inputData]);

  const onSubmitForm = () => {
    // Validate dates before submission
    if (!validateDates(inputData.startDate, inputData.endDate)) {
      return; // Don't submit if dates are invalid
    }

    const data = {
      currency: inputData.currency,
      cogs: Number(inputData.cogs),
      misc: Number(inputData.miscellaneous),
      fees: Number(inputData.total),
      product_id: inputData.product,
      date_start: inputData.startDate,
      date_end: inputData.endDate,
      product_source: "AMAZON",
    };

    if (onEditCogSuccess) {
      onEditCogSuccess(data);
    } else if (onSubmit) {
      try {
        dataEntryApi.uploadCogsData(data);
        onSubmit();
      } catch (error: any) {
        console.error("Failed to upload COGS:", error);

        const message =
          error?.response?.data?.message ||
          error?.message ||
          "An unexpected error occurred";

        toast.error(`Failed to upload COGS: ${message}`);
      }
    }
  };

  return (
    <div className={classNames(styles.addSkuContainer, className)}>
      <div className={styles.firstField}>
        <span className={styles.label}>PRODUCT</span>
        <Select
          placeholder="Select product"
          value={inputData.product as any}
          onChange={handleProductChange}
          options={productOptions}
          className={styles.fullWidthSelect}
        />
      </div>

      <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
        <LocalFormField
          val={inputData.cogs}
          onChange={(value) => handleFieldChange("cogs", value)}
          label="COGS"
          placeholder=""
          type="number"
          size="small"
          suffix="per unit"
          icon={<DollarSign size={20} color="#7C7C78" />}
        />
        <div className={styles.ad}>
          <span className={styles.label}>CURRENCY</span>
          <Select
            className={styles.fullWidthSelect}
            placeholder="Select currency"
            value={inputData.currency ?? undefined}
            onChange={handleCurrencyChange}
            options={[{ id: "USD", text: "USD" }]}
            disabled={true}
          />
        </div>
      </div>

      <div className={styles.dateRow}>
        <div className={styles.datePickerWrappertwo}>
          <span className={styles.dateLabel}>START DATE</span>
          <div className={styles.datePickerWithIcon}>
            <img
              src={CalendarIcon}
              alt="CalendarIcon"
              width={16}
              height={16}
              className={styles.prefixIcon}
            />
            <DatePicker
              className={`${styles.dateInput} ${dateError ? styles.dateError : ""}`}
              placeholder="01.01.2024"
              format="DD.MM.YYYY"
              suffixIcon={null}
              allowClear={false}
              onChange={(date) => handleDateChange("startDate", date)}
              value={inputData.startDate ? moment(inputData.startDate) : null}
              disabledDate={disabledStartDate}
            />
          </div>
        </div>
        <div className={styles.datePickerWrappertwo}>
          <span className={styles.dateLabel}>END DATE</span>
          <div className={styles.datePickerWithIcon}>
            <img
              src={CalendarIcon}
              alt="CalendarIcon"
              width={16}
              height={16}
              className={styles.prefixIcon}
            />
            <DatePicker
              className={`${styles.dateInput} ${dateError ? styles.dateError : ""}`}
              placeholder="31.12.2024"
              format="DD.MM.YYYY"
              suffixIcon={null}
              allowClear={false}
              onChange={(date) => handleDateChange("endDate", date)}
              value={inputData.endDate ? moment(inputData.endDate) : null}
              disabledDate={disabledEndDate}
            />
          </div>
        </div>
      </div>

      {/* Display date error message if exists */}
      {dateError && <div className={styles.errorMessage}>{dateError}</div>}

      <div style={{ display: "flex", gap: "10px" }}>
        <LocalFormField
          val={inputData.miscellaneous}
          onChange={(value) => handleFieldChange("miscellaneous", value)}
          label="MISCELLANEOUS COST"
          type="number"
          size="small"
          suffix="per unit"
          icon={<DollarSign size={20} color="#7C7C78" />}
        />
        <LocalFormField
          val={inputData.total}
          onChange={(value) => handleFieldChange("total", value)}
          label="TOTAL AMAZON FEES (OPTIONAL)"
          type="number"
          size="small"
          suffix="per unit"
          icon={<DollarSign size={20} color="#7C7C78" />}
        />
      </div>

      <div className={styles.actionButtons}>
        <button
          type="button"
          className={styles.uploadButton}
          onClick={onSubmitForm}
          disabled={
            !inputData.product ||
            !inputData.currency ||
            !inputData.startDate ||
            !inputData.endDate ||
            !inputData.cogs ||
            dateError !== null // Disable button if there's a date error
          }
        >
          {onEditCogSuccess ? "Save Changes" : "Add Data Entry"}
        </button>
      </div>
    </div>
  );
};
